import type { Plan } from '@/api/types'
import { formatPrice } from '@/utils/formatPrice'
import { PLURAL_FORMS, pluralizeRu, type PluralForms } from '@/utils/pluralizeRu'

/**
 * Формулировки тарифа Pro. Числа (цена, срок, лимиты) приходят из GET /billing/plans
 * через stores/plans.ts, а этот файл остаётся единственным местом самих текстов: они
 * функции от тарифа, чтобы цифры не разъезжались между онбордингом, пейволлом и кабинетом.
 *
 * Компоненты вызывают их не напрямую, а через геттеры стора — иначе шесть экранов будут
 * по-разному собирать одни и те же строки.
 */

/** Название тарифа. Объявлено здесь, а не в разделе кабинета: нужно фолбэку ниже. */
export const PRO_PLAN_NAME = 'PomogAI Pro'

/** Слаг тарифа Pro: ключ поиска в массиве /billing/plans и plan_id по умолчанию. */
export const PRO_PLAN_SLUG = 'pro_monthly'

const FALLBACK_PRICE_MINOR = 39900

/**
 * Тариф, на котором экраны работают, пока /billing/plans не ответил, упал или отдал
 * негодные данные. Значения — те же, что были захардкожены до появления ручки. Цифра
 * здесь может отстать от бэка, но пустое место вместо цены на кнопке хуже, чем цифра,
 * а расхождение с реальной суммой ловит ассерт перед открытием виджета.
 */
export const PRO_PLAN_FALLBACK: Plan = {
	name: PRO_PLAN_NAME,
	slug: PRO_PLAN_SLUG,
	priceMinor: FALLBACK_PRICE_MINOR,
	// Считаем, а не пишем: две цены в одной константе разъедутся на первой же правке.
	priceMajor: FALLBACK_PRICE_MINOR / 100,
	daysPeriod: 30,
	currency: 'RUB',
	limits: { text: 300, image: 15, song: 5 }
}

/**
 * Какое поле тарифа бэк ждёт в plan_id у POST /billing/checkout. По смыслу контракта это
 * slug — машинный идентификатор, тогда как name витринное («PomogAI Pro»). Ответа бэка
 * пока нет, поэтому переключатель: смена значения здесь — вся правка.
 * Признак того, что угадали неверно, — код PLAN_NOT_FOUND от чекаута.
 */
export const PLAN_ID_FIELD: 'slug' | 'name' = 'slug'

/**
 * Склонения для строк состава. Здесь, а не в PLURAL_FORMS: формы специфичны для этих
 * формулировок — в тарифе «300 текстовых запросов», а в баре остатков «300 текстов».
 */
const FEATURE_FORMS = {
	request: ['текстовый запрос', 'текстовых запроса', 'текстовых запросов'],
	image: ['изображение', 'изображения', 'изображений'],
	song: ['песня', 'песни', 'песен'],
	calendarDay: ['календарный день', 'календарных дня', 'календарных дней']
} as const satisfies Record<string, PluralForms>

function textCount(plan: Plan): string {
	return `${plan.limits.text} ${pluralizeRu(plan.limits.text, FEATURE_FORMS.request)}`
}

function imageCount(plan: Plan): string {
	return `${plan.limits.image} ${pluralizeRu(plan.limits.image, FEATURE_FORMS.image)}`
}

function songCount(plan: Plan): string {
	return `${plan.limits.song} ${pluralizeRu(plan.limits.song, FEATURE_FORMS.song)}`
}

/** «на 30 дней» — срок в обиходной форме. */
function daysCount(plan: Plan): string {
	return `${plan.daysPeriod} ${pluralizeRu(plan.daysPeriod, PLURAL_FORMS.day)}`
}

/** «30 календарных дней» — срок в формулировке примечаний. */
function calendarDays(plan: Plan): string {
	return `${plan.daysPeriod} ${pluralizeRu(plan.daysPeriod, FEATURE_FORMS.calendarDay)}`
}

/** Цена с символом валюты: «399 ₽». */
export function priceLabel(plan: Plan): string {
	return formatPrice(plan.priceMinor, plan.currency)
}

export function proCta(plan: Plan): string {
	return `Получить Pro за ${priceLabel(plan)}`
}

/** Последний пункт без числа: возможность есть или нет, считать в ней нечего. */
export function proFeatures(plan: Plan): string[] {
	return [textCount(plan), imageCount(plan), songCount(plan), 'Ассистент эмоциональной поддержки']
}

/**
 * Онбординг живёт на литералах, а не на тарифе с сервера. Экран показывается раньше,
 * чем отвечает /billing/plans, — цифра на кнопке успела бы смениться на глазах у
 * человека, который её в этот момент читает.
 *
 * TODO(billing): вернуть онбординг на proCta/proFeatures/proOnboardingFootnote, когда
 * тарифы будут готовы к моменту показа экрана.
 */
export const PRO_CTA = 'Получить Pro за 399 ₽'

export const PRO_FEATURES = ['300 текстовых запросов', '15 изображений', '5 песен', 'Ассистент эмоциональной поддержки']

export function proPaywallDescription(plan: Plan): string {
	return `В Pro вы получите ${imageCount(plan)}, ${textCount(plan)}, ${songCount(plan)} и ассистента эмоциональной поддержки на ${daysCount(plan)}.`
}

/**
 * Заголовки шторки, когда исчерпан лимит.
 * Меняются здесь, в одном месте.
 */
export const PRO_LIMIT_TITLES = {
	text: 'Бесплатные запросы закончились',
	image: 'Лимит изображений исчерпан',
	song: 'Лимит песен исчерпан'
} as const

/* ─────────── Личный кабинет (b2c) ─────────── */

export const FREE_PLAN_TITLE = 'Бесплатный тариф'

export const FREE_COUNTER_LABEL = 'Осталось бесплатных запросов'

/* Название тарифа в карточках профиля — PRO_PLAN_NAME — объявлено в шапке файла. */

/**
 * То же название, разобранное на части для трёхцветного вордмарка во Free-карточке:
 * «POMOG» тёмный, «AI» акцентный, «Pro» серый — как логотип в шапке.
 * Раскраска возможна только разметкой, поэтому строка лежит здесь по частям.
 */
export const PRO_PLAN_WORDMARK = {
	brand: 'POMOG',
	accent: 'AI',
	suffix: 'Pro'
} as const

export const PRO_PLAN_TITLE = 'Ваш тариф: Pro'

export function proLimitsNote(plan: Plan): string {
	return `Подписка с автопродлением действует ${calendarDays(plan)}. Остатки лимитов не переносятся.`
}

/** Сноска онбординга: тот же срок, другая формулировка. */
export function proOnboardingFootnote(plan: Plan): string {
	return `Лимиты действуют ${calendarDays(plan)}.`
}

export const PRO_PERIOD_LABEL = 'Действует до'

export const SUBSCRIPTION_LINK = 'Управление подпиской'

/** Экран управления подпиской и две шторки отмены автопродления. */
export const SUBSCRIPTION_TEXTS = {
	autoRenewOn: 'Автопродление включено',
	autoRenewOff: 'Автопродление отключено',
	nextCharge: 'Следующее списание',
	activeUntil: 'Активна до',
	amount: 'Сумма',
	paymentMethod: 'Способ оплаты',
	back: 'Назад',
	cancel: 'Отключить автопродление',
	resume: 'Возобновить подписку',
	footnote: 'Автопродление можно отключить в любой момент.'
} as const

/**
 * Дата окончания периода приходит из /usage и может отсутствовать (холодный стор,
 * бэк не отдал поле). Вместо пустого места подставляем нейтральную формулировку,
 * поэтому все тексты с датой — функции, а не константы.
 */
function until(dateLabel: string): string {
	return dateLabel || 'конца оплаченного периода'
}

/** Сноска в карточке подписки, когда автопродление включено. */
export function subscriptionNote(dateLabel: string): string {
	return `После отключения автопродления Pro и оставшиеся лимиты будут доступны до ${until(dateLabel)}. Новых списаний не будет.`
}

/** Та же сноска, когда автопродление уже отключено. */
export function subscriptionOffNote(dateLabel: string): string {
	return `${PRO_PLAN_NAME} и оставшиеся лимиты доступны до ${until(dateLabel)}. Новых списаний не будет.`
}

export function cancelConfirmDescription(dateLabel: string): string {
	return `${PRO_PLAN_NAME} останется активным до ${until(dateLabel)}. До этой даты вы сможете использовать оставшиеся лимиты. После отключения новых списаний не будет.`
}

export function cancelSuccessDescription(dateLabel: string): string {
	return `${PRO_PLAN_NAME} доступен до ${until(dateLabel)}. Новых списаний не будет.`
}

/**
 * Выгоды в удерживающей шторке
 */
export function proRetentionFeatures(plan: Plan): string[] {
	return [textCount(plan), `${imageCount(plan)} и ${songCount(plan)}`, 'Эмоциональная поддержка и история']
}

export const CANCEL_FLOW_TEXTS = {
	retention: {
		title: `Сохранить ${PRO_PLAN_NAME}?`,
		description: 'С подпиской у вас останутся все ключевые возможности сервиса:',
		remainingLabel: 'Сейчас осталось:',
		keep: 'Оставить Pro',
		proceed: 'Продолжить отмену'
	},
	confirm: {
		title: 'Отключить автопродление?',
		note: 'Подписку можно возобновить в личном кабинете в любое время.',
		keep: 'Сохранить подписку',
		confirm: 'Отключить автопродление',
		error: 'Не удалось отключить автопродление. Попробуйте ещё раз'
	},
	success: {
		title: 'Автопродление отключено',
		remainingTitle: 'До окончания доступа осталось',
		resume: 'Возобновить подписку',
		back: 'Вернуться в личный кабинет'
	}
} as const

/* ─────────── Шторка исчерпанного лимита (только Pro) ─────────── */

/** Все три лимита на нуле — четвёртый макет. Шире UsageResource, поэтому свой тип. */
export type LimitDialogVariant = 'text' | 'image' | 'song' | 'all'

export const LIMIT_DIALOG_TITLES: Record<LimitDialogVariant, string> = {
	text: 'Текстовые запросы закончились',
	image: 'Изображения закончились',
	song: 'Песни закончились',
	all: 'Все лимиты закончились'
}

/**
 * Описание с цифрами из среза /usage. Склонения зашиты в строку, а не считаются:
 * ресурсов ровно три, и каждый нуждается в своей формулировке («мелодий и песен»).
 */
export const LIMIT_DIALOG_DESCRIPTIONS = {
	text: (used: number, limit: number) =>
		`Вы использовали лимит ${used} из ${limit} текстовых запросов. Обновите Pro, чтобы получить еще ${limit} текстовых запросов.`,
	image: (used: number, limit: number) =>
		`Вы использовали лимит ${used} из ${limit} изображений. Обновите Pro, чтобы получить еще ${limit} изображений.`,
	song: (used: number, limit: number) =>
		`Вы использовали лимит ${used} из ${limit} мелодий и песен. Обновите Pro, чтобы получить еще ${limit} песен.`
} as const

export function limitDialogAllDescription(text: number, image: number, song: number): string {
	return `Вы использовали все доступные лимиты ${PRO_PLAN_NAME}: ${text} текстовых запросов, ${image} изображений и ${song} песен.`
}

export const LIMIT_DIALOG_NOTE =
	'Лимиты будут обновлены при следующем продлении подписки. Дату продления можно посмотреть в личном кабинете.'

/**
 * Подписи карточек с остатками. Два набора: в ряд из трёх «Текстовые запросы» не влезают,
 * поэтому на четвёртом макете они сокращены до «Тексты».
 */
export const LIMIT_DIALOG_CARD_LABELS = {
	text: 'Текстовые запросы',
	image: 'Изображения',
	song: 'Песни'
} as const

export const LIMIT_DIALOG_CARD_LABELS_SHORT = {
	text: 'Тексты',
	image: 'Изображения',
	song: 'Песни'
} as const

/** Слово после числа в карточке. Не склоняется — ни при 0, ни при 248. */
export const LIMIT_DIALOG_CARD_SUFFIX = 'осталось'

export const LIMIT_DIALOG_CLOSE_CTA = 'Продолжить бесплатно'

export const LIMIT_DIALOG_PROFILE_CTA = 'Перейти в личный кабинет'

/**
 * Показывать кнопку «Возобновить подписку». В v1 выключена: ручки включения
 * автопродления (auto_renew: true) ещё нет, а вести человека с активной подпиской
 * в чекаут нельзя — бэк ответит 409, и он увидит ошибку вместо возобновления.
 * Переключатель, а не удаление кода: экраны и тесты уже написаны.
 */
export const SUBSCRIPTION_RESUME_ENABLED = false

/**
 * Проверять остаток на клиенте до отправки запроса. false — прежнее поведение:
 * запрос уходит, шторку показывает 402 от сервера.
 * Требования бизнеса пока не финальны, поэтому переключатель, а не удаление кода.
 */
export const CLIENT_LIMIT_GATE = true

/* ─────────── Шторка несостоявшегося продления ─────────── */

/**
 * Запирать генерации при сорванном продлении (subscription.status: suspended).
 * Переключатель, а не жёсткий код: статус новый, и если бэк начнёт присылать его в
 * неожидаемых случаях, блок снимается одной строкой, не откатывая шторку и тесты.
 */
export const SUSPENDED_GATE_ENABLED = true

/**
 * Автопродление не прошло. Тексты без цифр, поэтому константы, а не функции от тарифа:
 * цена на кнопке идёт через plansStore.cta, а вторая кнопка — LIMIT_DIALOG_PROFILE_CTA выше.
 */
export const SUSPENDED_DIALOG_TEXTS = {
	title: 'Не удалось продлить подписку',
	description: `Автопродление ${PRO_PLAN_NAME} не произошло, поэтому Pro-доступ завершён.`,
	note: 'Без Pro недоступны расширенные лимиты и генерация изображений, песен, музыки и эмоциональная поддержка.'
} as const

/* ─────────── Шторки оплаты ─────────── */

/**
 * Три состояния, которые человек видит после закрытия платёжного виджета.
 * Четвёртого (неизвестный исход по истечении срока) в v1 нет — ждём вводных.
 */
export const PAYMENT_DIALOG_TEXTS = {
	confirming: {
		title: 'Ожидаем оплату',
		description:
			'Ожидаем статус оплаты чтобы открыть для вас генерацию изображений, музыки и песен, эмоциональную поддержку и историю запросов.',
		cta: 'ОК'
	},
	succeeded: {
		title: 'Pro активирован!',
		cta: 'Начать пользоваться!'
	},
	failed: {
		title: 'Ошибка оплаты',
		cta: 'Попробовать снова'
	}
} as const

/**
 * Причина отказа человеческим языком. Ключи — коды бэка; список открытый,
 * неизвестный код показывает общий текст и уходит в лог.
 */
export const PAYMENT_FAILURE_TEXTS: Record<string, string> = {
	card_declined: 'Банк отклонил платёж. Попробуйте другую карту',
	insufficient_funds: 'Недостаточно средств на карте',
	expired_card: 'Срок действия карты истёк',
	'3ds_failed': 'Не пройдено подтверждение банка',
	provider_error: 'Оплата временно недоступна, попробуйте позже'
}

/** Описание в шторке успешной оплаты: числа те же, что человек видел на кнопке. */
export function paymentSuccessDescription(plan: Plan): string {
	return `Теперь доступны ${textCount(plan)}, ${imageCount(plan)}, ${songCount(plan)} и ассистент эмоциональной поддержки на ${daysCount(plan)}.`
}

export const PAYMENT_FAILURE_FALLBACK = 'Что-то пошло не так. Вернитесь на предыдущий шаг и повторите попытку.'
